import { createFileRoute, Link } from "@tanstack/react-router";
import HVIHeader from "@/components/HVIHeader";
import HVIFooter from "@/components/HVIFooter";
import { useEffect, useMemo, useState } from "react";
import { SEED_CASES, STATUS_COLOR, type CaseStatus, type OutbreakCase } from "@/data/cases";
import { useCases } from "@/hooks/useCases";
import { buildSeo, SITE_URL, newsArticle } from "@/lib/seo";
import { useDynamicSeo } from "@/lib/useDynamicSeo";
import { getFlag } from "@/lib/countryIndex";
import { COUNTRY_DESCRIPTIONS } from "@/data/countryDescriptions";

export const Route = createFileRoute("/theater")({
  component: TheaterPage,
  head: () =>
    buildSeo({
      path: "/theater",
      title: "Country & Regional Hantavirus Case Detail",
      description:
        "Country-by-country hantavirus outbreak detail with city-level case mapping, fatality counts, status breakdown and timeline of confirmed, suspected and monitored cases.",
      image: "/og/theater.jpg",
      keywords: [
        "hantavirus by country",
        "regional outbreak detail",
        "hantavirus cases USA Argentina Chile",
        "puumala europe",
        "hantaan korea china",
        "case fatality breakdown",
      ],
      jsonLd: [
        {
          "@context": "https://schema.org",
          "@type": "CollectionPage",
          name: "Hantavirus Outbreak — Country & Case Detail",
          url: `${SITE_URL}/theater`,
          isPartOf: { "@type": "WebSite", url: SITE_URL, name: "Hantavirus International" },
          mainEntity: {
            "@type": "ItemList",
            numberOfItems: SEED_CASES.length,
            itemListElement: SEED_CASES.slice(0, 20).map((c, i) => ({
              "@type": "ListItem",
              position: i + 1,
              name: `${c.country}${c.city ? " — " + c.city : ""} (${c.status})`,
            })),
          },
        },
        ...SEED_CASES.filter((c) => c.status === "confirmed" || c.status === "fatal")
          .slice(0, 10)
          .map((c) =>
            newsArticle({
              url: `${SITE_URL}/theater#case-${c.id}`,
              headline: `Hantavirus case in ${c.country}${c.city ? " (" + c.city + ")" : ""}`,
              description: c.notes,
              datePublished: c.onsetDate ?? new Date().toISOString().slice(0, 10),
            }),
          ),
      ],
    }),
});

function TheaterPage() {
  const [MapView, setMapView] = useState<React.ComponentType<any> | null>(null);
  const [selected, setSelected] = useState<OutbreakCase | null>(null);
  const [selectedCountry, setSelectedCountry] = useState<string>("Netherlands");
  const { cases } = useCases();

  useEffect(() => {
    import("@/components/MapView").then((m) => setMapView(() => m.default));
  }, []);

  // Countries with cases
  const countries = useMemo(() => {
    const map = new Map<string, OutbreakCase[]>();
    for (const c of cases) {
      if (!map.has(c.country)) map.set(c.country, []);
      map.get(c.country)!.push(c);
    }
    return Array.from(map.entries()).sort((a, b) => b[1].length - a[1].length);
  }, [cases]);

  const countryCases = useMemo(() => cases.filter((c) => c.country === selectedCountry), [cases, selectedCountry]);

  const stats = useMemo(() => {
    // Deaths are counted as separate cases. Total = rows + deaths,
    // CFR = deaths / (rows + deaths). Matches the convention used across
    // the dashboard (see DailyTrendCharts, OutbreakComparison).
    const fatal = countryCases.reduce((s, c) => s + (c.deaths ?? 0), 0);
    const total = countryCases.length + fatal;
    const confirmed = countryCases.filter((c) => c.status === "confirmed").length;
    const suspected = countryCases.filter((c) => c.status === "suspected").length;
    const monitoring = countryCases.filter((c) => c.status === "monitoring").length;
    const cities = new Set(countryCases.map((c) => c.city).filter(Boolean)).size;
    const onboard = countryCases.filter((c) => c.onboard).length;
    const firstDate = countryCases
      .filter((c) => c.onsetDate)
      .map((c) => new Date(c.onsetDate!).getTime())
      .sort((a, b) => a - b)[0];
    const lastDate = countryCases
      .filter((c) => c.onsetDate)
      .map((c) => new Date(c.onsetDate!).getTime())
      .sort((a, b) => b - a)[0];
    const cfr = total > 0 ? ((fatal / total) * 100).toFixed(1) : "0";
    return { total, fatal, confirmed, suspected, monitoring, cities, onboard, firstDate, lastDate, cfr };
  }, [countryCases]);

  const flyTarget = useMemo(() => {
    if (countryCases.length === 0) return null;
    const lat = countryCases.reduce((s, c) => s + c.lat, 0) / countryCases.length;
    const lng = countryCases.reduce((s, c) => s + c.lng, 0) / countryCases.length;
    return { lat, lng, zoom: 5 };
  }, [countryCases]);

  const dateFmt = (ts: number) => new Date(ts).toLocaleDateString("en", { day: "2-digit", month: "short", year: "numeric" });

  // Dynamic SEO based on current selection (country + optional case).
  const seoTitle = selected
    ? `${selected.city || selected.country} — ${selected.status.toUpperCase()} hantavirus case`
    : `${selectedCountry} — Hantavirus outbreak detail`;
  const seoDesc = selected
    ? `${selected.status[0].toUpperCase()}${selected.status.slice(1)} hantavirus case in ${selected.city || selected.country}${selected.nationality ? ", " + selected.nationality + " national" : ""}${selected.onsetDate ? ", onset " + new Date(selected.onsetDate).toLocaleDateString("en", { day: "2-digit", month: "short", year: "numeric" }) : ""}. ${selected.notes}`
    : `Live ${selectedCountry} hantavirus outbreak detail: ${stats.total} cases, ${stats.fatal} fatal, ${stats.confirmed} confirmed, ${stats.suspected} suspected. CFR ${stats.cfr}%.`;
  const seoPath = selected
    ? `/theater?country=${encodeURIComponent(selectedCountry)}&case=${selected.id}`
    : `/theater?country=${encodeURIComponent(selectedCountry)}`;
  useDynamicSeo({ active: true, title: seoTitle, description: seoDesc, canonicalPath: seoPath });

  return (
    <div className="flex flex-col min-h-screen bg-background text-foreground font-sans">
      <HVIHeader />

      <h1 className="sr-only">{seoTitle}</h1>
      <main className="flex-1 flex flex-col md:flex-row overflow-hidden">
        {/* Sidebar */}
        <aside className="w-full md:w-80 flex-shrink-0 bg-card border-r border-border overflow-y-auto">
          <div className="p-4 border-b border-border">
            <Link to="/" className="inline-flex items-center gap-1 text-xs uppercase tracking-wider text-muted-foreground hover:text-hvi-primary transition">
              ← Global View
            </Link>
          </div>

          {/* Country selector */}
          <div className="p-4 border-b border-border">
            <span className="label-caps text-muted-foreground mb-2 block">Select Region</span>
            <div className="space-y-1">
              {countries.map(([country, cCases]) => (
                <button
                  key={country}
                  onClick={() => { setSelectedCountry(country); setSelected(null); }}
                  className={`w-full flex items-center justify-between px-3 py-2 text-sm transition border ${
                    selectedCountry === country
                      ? "border-hvi-primary bg-hvi-primary/10 text-hvi-primary font-semibold"
                      : "border-transparent hover:bg-background text-foreground"
                  }`}
                >
                  <span className="flex items-center gap-2">
                    <span>{getFlag(country)}</span>
                    <span>{country}</span>
                  </span>
                  <span className="font-data text-xs">{cCases.length}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Stats */}
          <div className="p-4 border-b border-border">
            <div className="flex items-center gap-2 mb-3">
              <span className="text-lg">{getFlag(selectedCountry)}</span>
              <h2 className="text-xl font-bold text-foreground">{selectedCountry}</h2>
            </div>

            {COUNTRY_DESCRIPTIONS[selectedCountry] && (
              <p className="text-xs text-muted-foreground leading-relaxed mb-3 border-l-2 border-hvi-primary/40 pl-2">
                {COUNTRY_DESCRIPTIONS[selectedCountry]}
              </p>
            )}

            <div className="grid grid-cols-2 gap-2 mb-3">
              <StatBox label="Total Cases" value={stats.total} borderColor="#ffb690" tooltip="Total number of reported cases in this country, including all statuses." />
              <StatBox label="Fatal" value={stats.fatal} borderColor="#ef4444" tooltip="Number of confirmed deaths caused by hantavirus infection." />
              <StatBox label="Confirmed" value={stats.confirmed} borderColor="#f97316" tooltip="Cases confirmed positive by PCR or serological testing." />
              <StatBox label="Suspected" value={stats.suspected} borderColor="#eab308" tooltip="Cases with matching symptoms, awaiting lab confirmation." />
              <StatBox label="Monitoring" value={stats.monitoring} borderColor="#3b82f6" tooltip="Individuals under active surveillance due to exposure risk." />
              <StatBox label="CFR" value={`${stats.cfr}%`} borderColor="#ef4444" tooltip="Case Fatality Rate — percentage of reported cases that resulted in death." />
            </div>

            <div className="space-y-2 text-xs text-muted-foreground">
              {stats.cities > 0 && (
                <InfoRow label="Affected Cities" value={String(stats.cities)} tooltip="Number of distinct cities with at least one reported case." />
              )}
              <InfoRow label="Ship-linked Cases" value={String(stats.onboard)} tooltip="Cases epidemiologically linked to the MV Hondius Antarctic cruise ship." />
              {stats.firstDate && (
                <InfoRow label="First Onset" value={dateFmt(stats.firstDate)} tooltip="Date of symptom onset for the earliest known case in this country." />
              )}
              {stats.lastDate && (
                <InfoRow label="Latest Onset" value={dateFmt(stats.lastDate)} tooltip="Date of symptom onset for the most recent case in this country." />
              )}
            </div>
          </div>

          <div className="p-4 border-b border-border">
            <Link
              to="/"
              hash="regions"
              className="w-full flex items-center justify-center gap-2 border border-hvi-primary text-hvi-primary py-2 text-xs uppercase tracking-wider hover:bg-hvi-primary/10 transition rounded-sm"
            >
              View Full Region Report →
            </Link>
          </div>

          {/* Case list */}
          <div className="p-4">
            <span className="label-caps text-muted-foreground mb-2 block">Case Details</span>
            <div className="space-y-2">
              {countryCases.map((c) => (
                <button
                  key={c.id}
                  onClick={() => setSelected(c)}
                  className={`w-full text-left border p-3 transition ${
                    selected?.id === c.id ? "border-hvi-primary bg-hvi-primary/5" : "border-border hover:bg-background/50"
                  }`}
                >
                  <div className="flex items-center gap-2 mb-1">
                    <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ background: STATUS_COLOR[c.status] }} />
                    <span className="text-sm font-semibold text-foreground">{c.city || c.country}</span>
                    <span className="ml-auto text-[10px] px-1.5 py-0.5" style={{ background: STATUS_COLOR[c.status] + "25", color: STATUS_COLOR[c.status] }}>
                      {c.status.toUpperCase()}
                    </span>
                  </div>
                  <div className="text-[11px] text-muted-foreground space-y-0.5 ml-4">
                    
                    {c.nationality && <div>Nationality: {c.nationality}</div>}
                    {c.onsetDate && <div>Onset: {new Date(c.onsetDate).toLocaleDateString("en", { day: "2-digit", month: "short", year: "numeric" })}</div>}
                  </div>
                </button>
              ))}
            </div>
          </div>
        </aside>

        {/* Map */}
        <div className="flex-1 relative min-h-[400px]">
          {MapView ? (
            <MapView
              cases={countryCases}
              flyTarget={flyTarget}
              onSelect={(c: OutbreakCase) => setSelected(c)}
              selectedCountry={selectedCountry}
              countryStats={{ [selectedCountry]: { cases: stats.total, deaths: stats.fatal } }}
            />
          ) : (
            <div className="h-full w-full flex items-center justify-center text-muted-foreground bg-background">
              Loading map…
            </div>
          )}

          {/* Selected case detail overlay */}
          {selected && (
            <div className="absolute top-3 right-3 z-[1000] w-72 max-w-[85vw] bg-card/95 border border-border shadow-2xl backdrop-blur">
              <div className="flex items-center justify-between px-4 py-3 border-b border-border">
                <span className="font-semibold text-sm">{selected.city || selected.country}</span>
                <button onClick={() => setSelected(null)} className="text-muted-foreground hover:text-foreground text-lg leading-none">×</button>
              </div>
              <div className="px-4 py-3 space-y-2">
                <span className="text-xs px-2 py-0.5 inline-block" style={{ background: STATUS_COLOR[selected.status] + "30", color: STATUS_COLOR[selected.status] }}>
                  {selected.status.toUpperCase()}
                </span>
                
                {selected.nationality && <div className="text-xs text-muted-foreground">Nationality · {selected.nationality}</div>}
                {selected.onsetDate && <div className="text-xs text-muted-foreground">Onset · {new Date(selected.onsetDate).toLocaleDateString("en", { day: "2-digit", month: "short", year: "numeric" })}</div>}
                {selected.onboard && <div className="text-xs text-purple-400">🚢 MV Hondius linked</div>}
                <p className="text-xs text-foreground leading-relaxed border-t border-border pt-2">{selected.notes}</p>
                {selected.source && <p className="text-[10px] text-muted-foreground">Source: {selected.source}</p>}
              </div>
            </div>
          )}
        </div>
      </main>

      <HVIFooter />
    </div>
  );
}

function StatBox({ label, value, borderColor, tooltip }: { label: string; value: string | number; borderColor: string; tooltip?: string }) {
  return (
    <div className="border-l-2 px-2.5 py-2 bg-background/50 group relative cursor-help" style={{ borderLeftColor: borderColor }} title={tooltip}>
      <div className="text-[9px] uppercase tracking-wider text-muted-foreground font-bold leading-tight">{label}</div>
      <div className="font-data text-lg font-semibold text-foreground leading-none mt-0.5">{value}</div>
      {tooltip && (
        <div className="absolute z-50 bottom-full left-1/2 -translate-x-1/2 mb-2 px-3 py-2 bg-card border border-border text-[11px] text-foreground leading-snug w-52 opacity-0 pointer-events-none group-hover:opacity-100 transition-opacity shadow-lg rounded-md">
          {tooltip}
          <div className="absolute top-full left-1/2 -translate-x-1/2 w-0 h-0 border-x-[5px] border-x-transparent border-t-[5px] border-t-border" />
        </div>
      )}
    </div>
  );
}

function InfoRow({ label, value, tooltip }: { label: string; value: string; tooltip?: string }) {
  return (
    <div className="flex justify-between group relative cursor-help" title={tooltip}>
      <span className="group-hover:text-foreground transition">{label}</span>
      <span className="font-data text-foreground">{value}</span>
      {tooltip && (
        <div className="absolute z-50 bottom-full left-1/2 -translate-x-1/2 mb-2 px-3 py-2 bg-card border border-border text-[11px] text-foreground leading-snug w-52 opacity-0 pointer-events-none group-hover:opacity-100 transition-opacity shadow-lg rounded-md">
          {tooltip}
        </div>
      )}
    </div>
  );
}
